/**
 * @fileoverview Package-owned violation dispatch for inspection modules.
 * @module inspection-renderer/modules
 *
 * Responsibilities:
 * - Dispatch violations module forms to the package-owned `<ViolationsModule>`.
 * - Return explicit alerts for contacts, which require a host render slot,
 *   and unknown module types.
 *
 * Dependencies:
 * - React 18 (peer).
 * - ViolationsModule and core-domain form types.
 * - `@firesquirrel/core-domain` form types.
 *
 * Architecture:
 * - Presentation-only. Pure dispatch.
 *
 * @component ModuleRenderer
 *
 * @contract ModuleRendererProps
 * Purpose: prop contract for the module dispatcher.
 */
import { type ReactNode } from 'react';
import type { InspectionForm } from '@firesquirrel/core-domain';
import { ViolationsModule } from './ViolationsModule';
import type { InspectorIdentity } from '../contracts/rendererContext';
import type { LoggedViolation } from '../violations/types';

export interface ModuleRendererProps {
  form: InspectionForm;
  /** Existing violations (filter by source='additional' when displaying). */
  violations: LoggedViolation[];
  readOnly?: boolean;
  onViolationsChange?: (nextViolations: LoggedViolation[]) => void;
  inspector?: InspectorIdentity;
  caseId?: string;
  currentEventIndex?: number;
}

export function ModuleRenderer(props: ModuleRendererProps): ReactNode {
  const { form, violations, readOnly, onViolationsChange, inspector, caseId, currentEventIndex } = props;

  // Header and standard forms are dispatched by InspectionRenderer.
  if (form.formKind !== 'module' || !form.moduleConfig) {
    return null;
  }

  const cfg = form.moduleConfig;
  switch (cfg.moduleType) {
    case 'contacts':
      return (
        <div role="alert" data-module-error="contacts-slot-missing" className="border border-red-200 bg-red-50 rounded-sm p-2 text-xs text-red-700">
          Contacts module renderer is unavailable in this surface.
        </div>
      );
    case 'violations':
      return (
        <ViolationsModule
          config={cfg}
          violations={violations.filter((v) => v.source === 'additional')}
          readOnly={readOnly}
          onViolationChanges={onViolationsChange}
          inspector={inspector}
          caseId={caseId}
          currentEventIndex={currentEventIndex}
        />
      );
    case 'smokeDetectors':
      return (
        <div data-module-smoke-detectors className="rounded-sm border border-blue-200 bg-blue-50/40 p-3 text-xs text-gray-700">
          <div className="font-semibold text-gray-800">Smoke Detectors</div>
          <div className="mt-1 text-gray-600">
            Manage household smoke detectors, search department inventory, and log detector installation or battery change events during the inspection.
          </div>
        </div>
      );
    default:
      return (
        <div role="alert" data-module-error="unknown" className="border border-red-200 bg-red-50 rounded-sm p-2 text-xs text-red-700">
          Unknown module type
        </div>
      );
  }
}
